import { Formik } from 'formik';
import { FC, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { FormModal, GFormFields } from '@components';
import { getRouteFormConfig, routeFormSchema } from '@constants';
import { ApiEndpoint, FormActionType, FormType } from '@enums';
import { useAppDispatch, useFormContent, useFormList, useFormProcedure } from '@hooks';
import { submitFormRequest, useCdnSummary } from '@store';
import i18n from '@translations';
import type { DialogBase, FormConfig, RouteConfigurationForm } from '@types';
import { convertOptionList, optionType, validateValueEqual } from '@utils';

export const RouteModal: FC<DialogBase> = ({ open, onClose }) => {
  const dispatch = useAppDispatch();
  const { id: application_id } = useParams<{ id: string }>();

  const formType = FormType.CdnRoute;

  const currentForm = useFormContent({ formType });
  const originList = useFormList({ formType: FormType.CdnOrigin, endpoint: ApiEndpoint.CUSTOM });
  const currentProcedure = useFormProcedure(formType);
  const cdnConfigurationSummary = useCdnSummary();

  const isEdit = validateValueEqual(currentProcedure, FormActionType.Edit);

  const domainOptions: optionType[] = useMemo(() => {
    const { options } = convertOptionList({
      originalOptions: cdnConfigurationSummary?.domain ?? [],
      defaultOptions: 'all',
      output: { value: 'domain', label: 'domain' },
    });

    return options;
  }, [cdnConfigurationSummary?.domain]);

  const originOptionList: optionType[] = useMemo(() => {
    const { options } = convertOptionList({
      originalOptions: originList,
      defaultOptions: '',
      output: { value: 'id', label: 'name' },
    });

    return options;
  }, [originList]);

  const schema = useMemo(() => routeFormSchema({ optionList: domainOptions }), [domainOptions]);

  const formConfig: FormConfig = useMemo(
    () =>
      getRouteFormConfig({
        isEdit,
        originOptionList,
        schema,
      }),
    [isEdit, originOptionList, schema, i18n.language],
  );

  const initialValues: RouteConfigurationForm = useMemo(
    () => ({
      name: currentForm?.name ?? '',
      origin: currentForm?.origin_id ?? '',
      conditions: currentForm?.conditions ?? [],
      connect_timeout: currentForm?.connect_timeout ?? 5,
      send_timeout: currentForm?.send_timeout ?? 5,
      read_timeout: currentForm?.read_timeout ?? 5,
    }),
    [currentForm],
  );

  const handleSubmit = useCallback(
    (values: RouteConfigurationForm): void => {
      const updatedValues = {
        ...values,
        conditions: values.conditions.map((condition) => ({
          ...condition,
          argument: condition.argument ?? '',
        })),
      };

      const formData = isEdit ? updatedValues : { application_id, ...updatedValues };

      const submitConfig = {
        formType,
        formData,
        globalOptions: {
          refetch_id: application_id,
        },
        ...(isEdit && {
          params: {
            content_id: currentForm.id,
          },
        }),
      };

      dispatch(submitFormRequest(submitConfig));
    },
    [dispatch, formType, isEdit, application_id, currentForm?.id],
  );

  const { inputList, label, validationSchema } = formConfig;

  if (!open || !inputList) return null;

  return (
    <Formik<RouteConfigurationForm>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <FormModal
        formType={formType}
        title={label?.title}
        description={label?.description}
        size="lg"
        open={open}
        confirmButton={{ children: label?.confirmButton }}
        onClose={onClose}
      >
        <GFormFields inputList={inputList} />
      </FormModal>
    </Formik>
  );
};
